import { useSEO } from "@/hooks/useSEO";
import { Link } from "react-router-dom";
import { Instagram, Youtube, Mail } from "lucide-react";
import profileImage from "@/assets/profile-delfu.jpg";
import { BRAND_HANDLE, BRAND_PERSON_NAME } from "@/lib/brandConfig";

const socialLinks = [
  { icon: Instagram, label: "Instagram", href: `https://instagram.com/${BRAND_HANDLE}` },
  { icon: Youtube, label: "YouTube", href: `https://www.youtube.com/@${BRAND_HANDLE}` },
];

const Usa = () => {
  useSEO("usa");

  return (
    <div className="min-h-screen bg-background">
      <main className="max-w-md mx-auto px-5 py-12 md:py-16">

        {/* Header */}
        <header className="text-center mb-10 animate-fade-in">
          <div className="relative inline-block mb-8">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-border via-muted to-border opacity-60" />
            <div className="relative w-28 h-28 rounded-full overflow-hidden ring-2 ring-background shadow-lg">
              <img
                src={profileImage}
                alt={`${BRAND_PERSON_NAME} — influencer USA portrait`}
                loading="eager"
                width={112}
                height={112}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl font-display font-medium text-foreground tracking-wide mb-3 italic">
            {BRAND_PERSON_NAME}
          </h1>
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="w-6 h-px bg-border" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
              Influencer · USA Market
            </p>
            <span className="w-6 h-px bg-border" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground/60 font-medium">
            Buenos Aires · Bilingual ES / EN
          </p>
        </header>

        {/* Bio */}
        <section className="mb-8 animate-fade-in" style={{ animationDelay: "0.1s" }}>
          <div className="space-y-4 text-center">
            <p className="text-sm leading-relaxed text-muted-foreground">
              I'm <strong className="text-foreground">{BRAND_PERSON_NAME}</strong>, an Argentine model and content creator building a bilingual community across the US, Latin America and Spain. Lifestyle, fitness and beauty content — in English and Spanish.
            </p>
            <p className="text-sm leading-relaxed text-muted-foreground">
              The US Hispanic market is the fastest-growing consumer segment in North America. My content bridges cultures and speaks authentically to both English-dominant and Spanish-dominant Latino audiences.
            </p>
          </div>
        </section>

        {/* Brand Partnership CTA */}
        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.2s" }}>
          <div className="border border-border rounded-xl p-5 text-center space-y-3">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
              Brand Partnerships · USA
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Available for US brand campaigns, product launches and long-term ambassador deals. Lifestyle, fitness, beauty and wellness verticals.
            </p>
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-foreground border border-foreground/20 rounded-lg px-4 py-2 hover:bg-foreground/5 transition-colors focus:outline-none focus:ring-2 focus:ring-foreground/20"
            >
              <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
              Get in touch
            </Link>
          </div>
        </section>

        {/* Socials */}
        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.3s" }}>
          <div className="flex justify-center gap-6">
            {socialLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 ui-transition-colors duration-200"
                aria-label={link.label}
              >
                <link.icon className="w-4 h-4" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </section>

        <div className="text-center animate-fade-in" style={{ animationDelay: "0.4s" }}>
          <Link
            to="/"
            className="inline-block text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-foreground/20 rounded"
          >
            ← Back to home
          </Link>
        </div>

        <footer className="mt-14 text-center animate-fade-in" style={{ animationDelay: "0.5s" }}>
          <p className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground/60">
            {`@${BRAND_HANDLE}`}
          </p>
        </footer>
      </main>
    </div>
  );
};

export default Usa;
